// Compare (SCENARIOS E3, PLAN-2026-10-03-research-tools.md §9, B-253): two to
// four players side by side, in this league's scoring — this week's
// projection, game and matchup; points a game over the season and the last
// three, and the season's points; the highest and lowest week; the next three
// games; injury and bye; and under them the seasons week by week, on a chart
// and in a table with each week's players together (B-376).
//
// THE NUMBERS BESIDE EACH OTHER, NEVER A PICK (cohesion check 9). The higher
// figure in each row is drawn in the accent and bold, so it reads without
// color, and it is the server's (`CompareView.highest`), so every client marks
// the same one. Nothing on the screen says which player to choose.
//
// A COLUMN PER PLAYER, the row's name as a caption across them: on a phone a
// label column would take a third of the width the numbers need. Up to four
// at every width, narrower on a phone.
//
// THE PAGE IS THE SLOTS (B-407, the plan of 2026-10-17 §2): two empty boxes
// that say Add a player before anybody is picked, and an empty one at the end
// of the heads until there are four, which is how the big sites' comparisons
// say what to do. Until there are two the search is on the page; after, an
// empty slot opens it in a sheet. From a desktop the chart sits beside the
// figures, and the table runs the width under both.
//
// THE TRAY IS THE COMPARISON OPENED OUT (B-375): whoever is picked on Players
// or a card is who this shows, and taking one out or adding one here changes
// the tray too, so leaving and coming back finds the same players. Opening
// Compare puts the tray's bar away (B-407).
//
// Simple keeps the projection, the games and the status (DETAIL_LEVELS.md);
// Standard adds the matchup cue and the season's figures; Everything adds the
// note under a projection that says where it came from.
//
// YOUR PLAYERS IN ONE PRESS (B-393): with nobody picked, a button for each
// position I have two or more players at puts them side by side, the four
// highest projected where I have more: the comparison a manager makes most,
// mine against mine, without ticking them one by one on Players.

import {
  COMPARE_MAX,
  failureOf,
  valueOf,
  type CompareFigure,
  type ComparePlayer,
  type CompareView,
  type CompareWeek,
  type PlanCell,
  type PlayerRowView,
} from '@fsp/domain';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAtLeast } from '../data/detail';
import { useCompare, usePlayers, useReaderZone } from '../data/hooks';
import { useActiveLeague } from '../data/LeaguesProvider';
import { useLeagueSource } from '../data/leagueSource';
import {
  EmptyState,
  GameLabel,
  PositionPill,
  ProTeamCode,
  StatusBadge,
  positionLabel,
} from '../kit';
import { Icon } from '../kit/Icon';
import {
  Button,
  Caption,
  IconButton,
  Line,
  ListRow,
  RowName,
  RowSub,
  Text,
  TextField,
} from '../kit/ui';
import { clubLabel, getSport } from '../sports/registry';
import { Term } from '../kit/Why';
import { useCompareTray } from '../shell/compareTray';
import { CompareWeeks, Marker } from './compare/CompareWeeks';
import { DetailSheet } from '../templates/DetailSheet';
import { useTier } from '../shell/useTier';
import { FromLeague } from './shared/FromLeague';
import { MatchupChip, matchupSentence } from './shared/MatchupChip';
import { num } from './shared/notComputed';
import { PlayerCard, inLeagueLine, type PlayerCardData } from './shared/PlayerCard';
import { Proj, projectionNote } from './shared/Projection';
import '../styles/screens/compare.css';
import { useTools } from '../data/tools';
import { injuryWords } from './shared/injuryWords';

/** The ids in the address, distinct and in order, no more than the comparison holds. */
export function comparedIds(raw: string | null): string[] {
  if (raw === null) return [];
  return [
    ...new Set(
      raw
        .split(',')
        .map((x) => x.trim())
        .filter((x) => x.length > 0),
    ),
  ].slice(0, COMPARE_MAX);
}

export function CompareScreen() {
  const [params, setParams] = useSearchParams();
  const ids = comparedIds(params.get('players'));
  const source = useLeagueSource();
  const snapshot = useCompare(source === 'api' ? ids : []);
  const tier = useTier();
  const wide = tier === 'desktop' || tier === 'large';
  const tray = useCompareTray();
  const [adding, setAdding] = useState(false);
  // ARRIVED WITH NOBODY IN THE ADDRESS, the tray's players: on arrival, and
  // again when the league switcher brings another league's Compare up in
  // the same place. With players in the address, they are the tray's from
  // then on (a shared link included) — UNLESS SOMEBODY WAS ADDED SINCE
  // Compare last showed them (the bar is out): then the tray leads, or a
  // Players tab that remembers an older Compare would put its players back
  // and drop the one just added (the review of B-407).
  const view = valueOf(snapshot);
  const fromTray = tray.picked.map((p) => p.id).join(',');
  const added = tray.shown;
  useEffect(() => {
    if (fromTray !== '' && fromTray !== ids.join(',') && (ids.length === 0 || added)) {
      setParams(
        (p) => {
          const out = new URLSearchParams(p);
          out.set('players', fromTray);
          return out;
        },
        { replace: true },
      );
    }
    // Per league, not per render: later the address leads.
  }, [tray.leagueId]);
  // THE NAMES FROM THE VIEW, for an address that arrived on its own: this
  // league's view only, never the last league's still on screen.
  useEffect(() => {
    if (added || view === null || view.leagueId !== tray.leagueId) return;
    const shown = view.players.map((p) => ({ id: p.player.id, name: p.player.name }));
    if (shown.map((p) => p.id).join(',') !== fromTray) tray.set(shown);
  }, [view]);
  // THE BAR GOES ONCE COMPARE SHOWS THEM (B-407), until the next add.
  useEffect(() => {
    if (tray.shown) tray.settle();
  }, [tray.leagueId, tray.shown]);
  const set = (next: string[]) =>
    setParams(
      (p) => {
        const out = new URLSearchParams(p);
        if (next.length === 0) out.delete('players');
        else out.set('players', next.join(','));
        return out;
      },
      { replace: true },
    );
  // THE TRAY MOVES WITH THE ADDRESS AT ONCE, not when the next read lands:
  // a read that fails, or a tab tapped first, would leave them apart.
  const add = (p: { id: string; name: string }) => {
    setAdding(false);
    if (ids.includes(p.id)) return;
    set([...ids, p.id].slice(0, COMPARE_MAX));
    if (!tray.has(p.id)) tray.set([...tray.picked, p]);
  };
  const remove = (id: string) => {
    set(ids.filter((x) => x !== id));
    tray.remove(id);
  };
  /** A whole comparison at once: the address and the tray together. */
  const compareAll = (players: { id: string; name: string }[]) => {
    set(players.map((p) => p.id));
    tray.set(players);
  };

  if (source === 'sim') {
    return (
      <EmptyState>
        Compare reads a real league’s season. The tour plays one week, so there is nothing to put
        side by side.
      </EmptyState>
    );
  }

  // THE VIEW FOR THESE PLAYERS, never the last one still on screen while the
  // next is read.
  const current =
    view !== null && view.players.map((p) => p.player.id).join(',') === ids.join(',') ? view : null;

  const body =
    ids.length < 2 ? (
      <Starting
        first={current?.players[0] ?? null}
        named={ids[0] === undefined ? null : (tray.picked.find((p) => p.id === ids[0]) ?? null)}
        firstId={ids[0] ?? null}
        taken={ids}
        onPick={add}
        onRemove={remove}
        onCompare={compareAll}
      />
    ) : (
      <FromLeague snapshot={snapshot}>
        {(view) => (
          <Comparison
            view={view}
            wide={wide}
            onRemove={remove}
            onAdd={ids.length < COMPARE_MAX ? () => setAdding(true) : null}
          />
        )}
      </FromLeague>
    );

  return (
    <div className="fsp-screen">
      <div className="fsp-regions fsp-list-alone">
        <section className="fsp-region fsp-compare-main" aria-label="Compare">
          {body}
          {tier === 'phone' ? <div className="fsp-list-chip-clearance" aria-hidden="true" /> : null}
        </section>
      </div>
      <DetailSheet opened={adding} onClose={() => setAdding(false)} title="Add a player">
        <Picker first={current?.players[0] ?? null} taken={ids} onPick={add} autoFocus />
      </DetailSheet>
    </div>
  );
}

/** "J. Chase" from "Ja'Marr Chase", for a name the view has not served yet. */
function shortOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  return `${parts[0]![0]}. ${parts.slice(1).join(' ')}`;
}

/**
 * Before there are two: what Compare is for, two slots — the one picked and
 * an empty one, or two empty — and the search on the page, so the next pick
 * is where the eye already is (B-407). An empty slot takes the search.
 */
function Starting({
  first,
  named,
  firstId,
  taken,
  onPick,
  onRemove,
  onCompare,
}: {
  /** The one picked, as the server reads him; null before it answers. */
  first: ComparePlayer | null;
  /** His name from the tray, while the read is on its way. */
  named: { id: string; name: string } | null;
  firstId: string | null;
  taken: readonly string[];
  onPick: (p: { id: string; name: string }) => void;
  onRemove: (id: string) => void;
  onCompare: (players: { id: string; name: string }[]) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const toSearch = () => box.current?.querySelector('input')?.focus();
  const short = first?.player.shortName ?? (named ? shortOf(named.name) : null);
  return (
    <div className="fsp-compare-start">
      <div className="fsp-compare-intro">
        <h2 className="fsp-disp fsp-compare-title">Compare players</h2>
        <Text tone="secondary" block>
          {`Pick two players, or up to ${COMPARE_MAX}, to see them side by side: this week, the season, and week by week.`}
        </Text>
      </div>
      <div className="fsp-compare-slots">
        {firstId === null ? (
          <EmptySlot label="Add a player" onClick={toSearch} />
        ) : first !== null ? (
          <PlayerHead p={first} series={0} onRemove={onRemove} />
        ) : (
          <div className="fsp-compare-head" data-series={0}>
            <div className="fsp-compare-head-top">
              <span className="fsp-compare-head-mark">
                <Marker series={0} size={12} />
              </span>
              <IconButton
                icon="close"
                label={`Remove ${named?.name ?? 'this player'}`}
                variant="quiet"
                size="sm"
                onClick={() => onRemove(firstId)}
              />
            </div>
            <span className="fsp-compare-name fsp-disp">{short ?? '…'}</span>
          </div>
        )}
        <EmptySlot
          label={short === null ? 'Add a player' : `Add a player to compare with ${short}`}
          onClick={toSearch}
        />
      </div>
      <div ref={box}>
        {/* A NEW SEARCH FOR THE NEXT PICK: what was typed to find the first
            would keep its other matches under the box that offers his
            position's list. */}
        <Picker
          key={firstId ?? 'none'}
          first={first}
          taken={taken}
          onPick={onPick}
          extra={firstId === null ? <YourPlayers onCompare={onCompare} /> : null}
        />
      </div>
    </div>
  );
}

/** An empty slot: a box that says what goes in it, and takes you to the search. */
function EmptySlot({
  label,
  onClick,
  compact = false,
}: {
  label: string;
  onClick: () => void;
  /** A phone's fourth column: the plus and one word. */
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      className="fsp-compare-empty"
      data-compact={compact || undefined}
      aria-label={compact ? label : undefined}
      onClick={onClick}
    >
      <Icon name="add" />
      <span>{compact ? 'Add' : label}</span>
    </button>
  );
}

/** A player's head: his color and shape, position, name (his card), club, status and team. */
function PlayerHead({
  p,
  series,
  onRemove,
  onOpen,
  role,
}: {
  p: ComparePlayer;
  series: number;
  onRemove: (id: string) => void;
  onOpen?: () => void;
  role?: 'columnheader';
}) {
  const zone = useReaderZone();
  return (
    <div className="fsp-compare-head" role={role} data-series={series}>
      <div className="fsp-compare-head-top">
        {/* HIS COLOR AND SHAPE, the chart's and the table's below. */}
        <span className="fsp-compare-head-mark">
          <Marker series={series} size={12} />
          {p.player.positions[0] ? <PositionPill pos={p.player.positions[0]} size="sm" /> : null}
        </span>
        <IconButton
          icon="close"
          label={`Remove ${p.player.name}`}
          variant="quiet"
          size="sm"
          onClick={() => onRemove(p.player.id)}
        />
      </div>
      {onOpen ? (
        <button type="button" className="fsp-compare-name fsp-disp" onClick={onOpen}>
          {p.player.shortName}
        </button>
      ) : (
        <span className="fsp-compare-name fsp-disp">{p.player.shortName}</span>
      )}
      <div className="fsp-compare-team">
        <ProTeamCode code={p.player.proTeam} />
        <StatusBadge player={p.player} />
      </div>
      <div className="fsp-sub fsp-compare-held">
        {p.inLeague.kind === 'owned'
          ? p.inLeague.team.name
          : p.inLeague.kind === 'waivers' && zone === null
            ? 'On waivers'
            : inLeagueLine(p.inLeague, zone ?? 'UTC').replace(/\.$/, '')}
      </div>
    </div>
  );
}

/**
 * A button for each position I have two or more players at, in the sport's
 * order, putting them side by side: the four highest projected where I have
 * more (B-393). Nothing while my roster is still being read.
 */
function YourPlayers({
  onCompare,
}: {
  onCompare: (players: { id: string; name: string }[]) => void;
}) {
  const tools = useTools();
  const league = useActiveLeague();
  const read = usePlayers({
    availability: 'mine',
    positions: [],
    q: '',
    // By points a game in a league that keeps no projections (§15c).
    sort: tools.projections ? 'projection' : 'average',
    reverse: false,
    cursor: null,
    limit: 50,
  });
  const rows = valueOf(read.snapshot)?.items ?? [];
  const by = new Map<string, PlayerRowView[]>();
  for (const r of rows) {
    const pos = r.player.positions[0];
    if (pos !== undefined) by.set(pos, [...(by.get(pos) ?? []), r]);
  }
  // The sport's positions, in its order and by its names: "Your running backs".
  const defs = league === null ? [] : getSport(league.league.sport as never).positions;
  const order = defs.map((p) => p.id as string);
  const rank = (pos: string) => (order.includes(pos) ? order.indexOf(pos) : order.length);
  const plural = (pos: string) => {
    const name = defs.find((p) => p.id === pos)?.name.toLowerCase();
    return name === undefined ? `${positionLabel(pos)}s` : `${name}s`;
  };
  const groups = [...by]
    .filter(([, list]) => list.length >= 2)
    .sort(([a], [b]) => rank(a) - rank(b));
  if (groups.length === 0) return null;
  return (
    <div className="fsp-compare-starts">
      <Caption>Your players, side by side</Caption>
      <Line gap={2} wrap>
        {groups.map(([pos, list]) => (
          <Button
            key={pos}
            size="xs"
            variant="soft"
            onClick={() =>
              onCompare(
                list.slice(0, COMPARE_MAX).map((r) => ({ id: r.player.id, name: r.player.name })),
              )
            }
          >
            {`Your ${plural(pos)}`}
          </Button>
        ))}
      </Line>
    </div>
  );
}

// ── The comparison ──────────────────────────────────────────────────────────

function Comparison({
  view,
  wide,
  onRemove,
  onAdd,
}: {
  view: CompareView;
  /** A desktop's room: the chart beside the figures. */
  wide: boolean;
  onRemove: (id: string) => void;
  /** The empty slot at the end of the heads, while there is room for one. */
  onAdd: (() => void) | null;
}) {
  const tools = useTools();
  const standard = useAtLeast('standard');
  const everything = useAtLeast('pro');
  const phone = useTier() === 'phone';
  const zone = useReaderZone();
  const [card, setCard] = useState<PlayerCardData | null>(null);
  const players = view.players;
  const columns = players.length + (onAdd ? 1 : 0);
  const high = (f: CompareFigure, id: string) => view.highest[f]?.includes(id) ?? false;

  const row = (title: string, cell: (p: ComparePlayer) => ReactNode) => (
    <div className="fsp-compare-row" role="row">
      <div className="fsp-compare-label" role="rowheader">
        <Caption>{title}</Caption>
      </div>
      {players.map((p) => (
        <div key={p.player.id} className="fsp-compare-cell" role="cell">
          {cell(p)}
        </div>
      ))}
      {onAdd ? <div className="fsp-compare-cell" role="cell" aria-hidden /> : null}
    </div>
  );

  const figures = (
    <div
      className="fsp-compare"
      role="table"
      aria-label="Players side by side"
      data-columns={columns}
    >
      <div className="fsp-compare-row fsp-compare-heads" role="row">
        {players.map((p, i) => (
          <PlayerHead
            key={p.player.id}
            p={p}
            series={i}
            role="columnheader"
            onRemove={onRemove}
            onOpen={() =>
              setCard({
                player: p.player,
                game: p.game,
                projection: p.projection,
                basis: p.basis,
                points: null,
                statLine: null,
              })
            }
          />
        ))}
        {/* ROOM FOR ONE MORE (B-407): an empty slot at the end of the
            heads at every width, a plus and a word in a phone's fourth. */}
        {onAdd ? (
          <div className="fsp-compare-head fsp-compare-add" role="columnheader">
            <EmptySlot label="Add a player" onClick={onAdd} compact={phone && columns >= 4} />
          </div>
        ) : null}
      </div>

      {/* Not in a league that keeps no projections (RULES_SCHEMA §15c). */}
      {tools.projections
        ? row(view.week ? `${view.week.label} · projected` : 'Projected', (p) => (
            <>
              <Figure value={num(p.projection)} high={high('projection', p.player.id)} big />
              {everything && projectionNote(p.projection, p.basis) !== null ? (
                <div className="fsp-sub fsp-compare-note">
                  {projectionNote(p.projection, p.basis)}
                </div>
              ) : null}
            </>
          ))
        : null}
      {row(view.week ? `${view.week.label} · game` : 'Game', (p) => (
        <div className="fsp-compare-game">
          <GameLabel game={p.game} />
          <MatchupChip
            cue={p.matchup}
            position={p.player.positions[0]}
            opponent={p.game?.opponent}
          />
        </div>
      ))}
      {standard
        ? row('Points a game · season', (p) => (
            <>
              <Figure
                value={p.perGame === null ? '—' : p.perGame.toFixed(1)}
                high={high('perGame', p.player.id)}
              />
              {/* Under the figure: four columns on a phone ran it into the next. */}
              <div className="fsp-sub">
                {p.games} {p.games === 1 ? 'game' : 'games'}
              </div>
            </>
          ))
        : null}
      {standard
        ? row('Points a game · last 3', (p) => (
            <Figure
              value={p.lastThree === null ? '—' : p.lastThree.toFixed(1)}
              high={high('lastThree', p.player.id)}
            />
          ))
        : null}
      {standard
        ? row('Season points', (p) => (
            <Figure
              value={p.points === null ? '—' : p.points.toFixed(1)}
              high={view.highestPoints.includes(p.player.id)}
            />
          ))
        : null}
      {standard
        ? row('Highest week', (p) => <Week week={p.high} high={high('high', p.player.id)} />)
        : null}
      {standard
        ? row('Lowest week', (p) => <Week week={p.low} high={high('low', p.player.id)} />)
        : null}
      {view.ahead.length > 0
        ? row('Next', (p) => (
            // Its own width decides whether the weeks sit side by side or
            // one over the next (B-521).
            <div className="fsp-compare-ahead-box">
              <div className="fsp-compare-ahead">
                {p.ahead.map((c, n) => (
                  <Ahead
                    key={c.period}
                    cell={c}
                    label={view.ahead[n]?.label ?? ''}
                    position={p.player.positions[0] ?? null}
                    tint={standard}
                  />
                ))}
              </div>
            </div>
          ))
        : null}
      {row('Status', (p) => (
        <div className="fsp-compare-status">
          <span>{p.player.injury ? injuryWords(p.player.injury, zone) : 'No designation'}</span>
          <span className="fsp-sub">{byeWords(p)}</span>
        </div>
      ))}
      <PlayerCard data={card} onClose={() => setCard(null)} />
    </div>
  );

  // FROM A DESKTOP, THE CHART BESIDE THE FIGURES (the plan of 2026-10-17
  // §2.2), and the week-by-week table the whole width under both.
  return wide ? (
    <>
      <div className="fsp-compare-top">
        <div className="fsp-compare-figs">{figures}</div>
        <div className="fsp-compare-chartside">
          <CompareWeeks view={view} parts="chart" />
        </div>
      </div>
      <CompareWeeks view={view} parts="table" />
    </>
  ) : (
    <>
      {figures}
      <CompareWeeks view={view} />
    </>
  );
}

function byeWords(p: ComparePlayer): string {
  const bye = p.bye;
  if (bye === null) return 'No club';
  switch (bye.state) {
    case 'week':
      return `Bye ${bye.label}`;
    case 'none':
      return 'No bye left';
    case 'unknown':
      return 'Bye not on the calendar yet';
  }
}

/** A figure, the higher in its row in the accent and bold, and said so to a screen reader. */
function Figure({ value, high, big = false }: { value: string; high: boolean; big?: boolean }) {
  return (
    <span
      className="fsp-num fsp-compare-figure"
      data-high={high || undefined}
      data-big={big || undefined}
    >
      {value}
      {high ? <span className="fsp-sr"> (highest)</span> : null}
    </span>
  );
}

function Week({ week, high }: { week: CompareWeek | null; high: boolean }) {
  if (week === null || week.points === null) return <span className="fsp-muted">—</span>;
  return (
    <>
      <Figure value={week.points.toFixed(1)} high={high} />
      <span className="fsp-sub"> {week.label.replace(/^Week\s+/i, 'Wk ')}</span>
    </>
  );
}

/** One week ahead: the opponent tinted by the matchup's third, BYE, or a dash. */
function Ahead({
  cell,
  label,
  position,
  tint,
}: {
  cell: PlanCell;
  label: string;
  position: string | null;
  tint: boolean;
}) {
  const short = label.replace(/^Week\s+/i, 'Wk ');
  const text =
    cell.state === 'game'
      ? `${cell.home ? '' : '@'}${cell.opponent === null ? '' : clubLabel(cell.opponent)}`
      : cell.state === 'bye'
        ? 'BYE'
        : '—';
  const sentence =
    cell.state === 'game' && cell.matchup && cell.opponent
      ? matchupSentence(cell.matchup, position, cell.opponent)
      : cell.state === 'tbd'
        ? `${label}: not on the calendar yet`
        : undefined;
  const face = (
    <>
      <span className="fsp-sub">{short}</span>
      <span>{text}</span>
    </>
  );
  // THE MATCHUP IN WORDS WHEN ASKED (B-374): a native title, which a finger
  // never reaches, left a phone with letters and a tint (the audit of
  // 2026-10-17).
  return (
    <span
      className="fsp-compare-ahead-cell"
      data-state={cell.state}
      data-band={tint && cell.matchup ? cell.matchup.band : undefined}
    >
      {sentence === undefined ? (
        face
      ) : (
        <Term tip={sentence} name={`${short} ${text}`} className="fsp-compare-ahead-term">
          {face}
        </Term>
      )}
    </span>
  );
}

// ── Adding a player ─────────────────────────────────────────────────────────

/**
 * Search the league's players by name or club. Before anything is typed, the
 * highest projected at the first player's position, which is who a manager
 * torn on a start is usually weighing him against, or with nobody picked the
 * highest projected this week; `extra` sits over that list until a search.
 */
function Picker({
  first,
  taken,
  onPick,
  autoFocus = false,
  extra = null,
}: {
  first: ComparePlayer | null;
  taken: readonly string[];
  onPick: (p: { id: string; name: string }) => void;
  /** In a sheet, the keyboard comes with it. */
  autoFocus?: boolean;
  extra?: ReactNode;
}) {
  const tools = useTools();
  const [q, setQ] = useState('');
  const position = first?.player.positions[0];
  const typed = q.trim().length > 0;
  const read = usePlayers({
    availability: 'all',
    positions: typed || position === undefined ? [] : [position],
    q,
    // By points a game in a league that keeps no projections (§15c).
    sort: tools.projections ? 'projection' : 'average',
    reverse: false,
    cursor: null,
    limit: 12,
  });
  const page = valueOf(read.snapshot);
  const failed = page === null ? failureOf(read.snapshot) : null;
  const rows = (page?.items ?? []).filter((r) => !taken.includes(r.player.id));
  return (
    <div className="fsp-compare-picker">
      <TextField
        label="Search players"
        hideLabel
        placeholder="Search by name or club"
        icon="search"
        value={q}
        onChange={setQ}
        autoFocus={autoFocus}
      />
      {!typed ? extra : null}
      {!typed ? (
        <Caption>
          {tools.projections
            ? position !== undefined
              ? `Highest projected at ${positionLabel(position)}`
              : 'Highest projected this week'
            : position !== undefined
              ? `Most points a game at ${positionLabel(position)}`
              : 'Most points a game'}
        </Caption>
      ) : null}
      <div className="fsp-surface fsp-compare-results">
        {failed !== null ? (
          <p className="fsp-sub fsp-compare-full">{failed.message}</p>
        ) : page === null ? (
          <p className="fsp-sub fsp-compare-full">Reading the league’s players…</p>
        ) : rows.length === 0 ? (
          <p className="fsp-sub fsp-compare-full">
            {typed ? 'Nobody by that name.' : 'Nobody else to add here.'}
          </p>
        ) : (
          rows.map((r) => <PickRow key={r.player.id} row={r} onPick={onPick} />)
        )}
      </div>
    </div>
  );
}

function PickRow({
  row,
  onPick,
}: {
  row: PlayerRowView;
  onPick: (p: { id: string; name: string }) => void;
}) {
  const tools = useTools();
  return (
    <ListRow
      lead={
        row.player.positions[0] ? (
          <PositionPill pos={row.player.positions[0]} size="sm" />
        ) : undefined
      }
      trail={
        <span className="fsp-num">
          {tools.projections ? <Proj>{num(row.projection)}</Proj> : `${num(row.seasonAvg)} a game`}
        </span>
      }
      onClick={() => onPick({ id: row.player.id, name: row.player.name })}
    >
      <RowName marks={<StatusBadge player={row.player} />}>{row.player.name}</RowName>
      <RowSub>
        {row.player.proTeam === null ? 'No club' : clubLabel(row.player.proTeam)}
        {row.game?.opponent
          ? ` · ${row.game.home ? 'vs' : 'at'} ${clubLabel(row.game.opponent)}`
          : ''}
      </RowSub>
    </ListRow>
  );
}
